import { Component, OnInit, computed, signal, resource, model, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AdminDashboardService } from '../admin-dashboard.service';
import { SyncError } from '../admin-dashboard.model';
import { CommonService } from 'src/app/services/common.service';
import { ErrorService } from 'src/app/common/errors/error.service';
import { SearchInputComponent } from 'src/app/common/general/search-input/search-input.component';
import { NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownItem } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';


@Component({
  selector: 'app-sync-error-log',
  templateUrl: './sync-error-log.component.html',
  styleUrl: './sync-error-log.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SearchInputComponent, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownItem, FormsModule, DatePipe]
})
export class SyncErrorLogComponent implements OnInit {
  
  searchText = model<string>('');
  
  // Signal to receive filtered results from search component
  searchFilteredErrors = signal<SyncError[]>([]);

  // Signals for reactive filtering
  timeFilter = signal<'30d' | '7d' | '24h'>('30d');
  customerFilter = signal<number | null>(null);
  
  // Selected error ID signal
  selectedErrorId = signal<number | null>(null);

  // Resource signal for loading errors
  errorsResource = resource({
    params: () => ({
      fromDate: this.getFromDate(),
      toDate: new Date()
    }),
    loader: async ({ params }) => {
      return new Promise<SyncError[]>((resolve, reject) => {
        this.adminDashboardService.getSyncErrors(
          params.fromDate, 
          params.toDate
        ).subscribe({
          next: (errors) => resolve(errors),
          error: (err) => {
            // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
            console.error('Error loading sync errors:', err);
            reject(err);
          }
        });
      });
    }
  });

  // Computed signal from resource
  errorsSignal = computed(() => this.errorsResource.value() ?? [] as SyncError[]);
  isLoading = computed(() => this.errorsResource.isLoading());
  
  // Computed signal for selected error - finds error by ID in the list
  selectedError = computed(() => {
    const errorId = this.selectedErrorId();
    if (errorId === null) {
      return null;
    }
    
    const errors = this.errorsSignal();
    const loading = this.isLoading();
    
    // If still loading, implicitly wait (computed will re-evaluate when data loads)
    if (loading) {
      return null;
    }
    
    // Find error in list, return null if not found
    return errors.find(e => e.logId === errorId) ?? null;
  });

  private getFromDate(): Date {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  }

  // Helper method for time range filtering
  private isWithinTimeRange = (timestamp: Date, filter: '30d' | '7d' | '24h'): boolean => {
    const now = new Date();
    const errorDate = new Date(timestamp);
    const diffMs = now.getTime() - errorDate.getTime();

    switch (filter) {
      case '24h':
        return diffMs <= 24 * 60 * 60 * 1000;
      case '7d':
        return diffMs <= 7 * 24 * 60 * 60 * 1000;
      case '30d':
        return diffMs <= 30 * 24 * 60 * 60 * 1000;
      default:
        return true;
    }
  };

  // Computed signals for filtering - chain them
  // First apply customer filter (independent of time)
  customerOnlyFilteredErrors = computed(() => {
    const errors = this.errorsSignal();
    const customerId = this.customerFilter();
    
    if (customerId === null) {
      return errors;
    }
    return errors.filter(e => e.kundeId === customerId);
  });

  // Then apply time filter
  timeFilteredErrors = computed(() => {
    const errors = this.customerOnlyFilteredErrors();
    const filter = this.timeFilter();
    return errors.filter(e => this.isWithinTimeRange(e.timestamp, filter));
  });

  // Customer filtered is the same as time filtered (for search input)
  customerFilteredErrors = computed(() => {
    return this.timeFilteredErrors();
  });

  // Finally apply search filter - use search component's output
  filteredErrors = computed(() => {
    const searchFiltered = this.searchFilteredErrors();
    // If search hasn't been initialized yet, use time filtered errors
    if (searchFiltered.length === 0 && this.searchText() === '') {
      return this.timeFilteredErrors();
    }
    return searchFiltered;
  });

  // Count signals - based on customer filter only
  thirtyDayCount = computed(() => this.customerOnlyFilteredErrors().filter(e => this.isWithinTimeRange(e.timestamp, '30d')).length);
  sevenDayCount = computed(() => this.customerOnlyFilteredErrors().filter(e => this.isWithinTimeRange(e.timestamp, '7d')).length);
  twentyFourHourCount = computed(() => this.customerOnlyFilteredErrors().filter(e => this.isWithinTimeRange(e.timestamp, '24h')).length);

  // Computed signal for unique customers from errors
  availableCustomers = computed(() => {
    const errors = this.errorsSignal();
    const customerMap = new Map<number, string>();
    
    errors.forEach(e => {
      if (e.customerName && !customerMap.has(e.kundeId)) {
        customerMap.set(e.kundeId, e.customerName);
      }
    });
    
    return Array.from(customerMap.entries())
      .map(([customerId, customerName]) => ({ customerId, customerName }))
      .sort((a, b) => a.customerName.localeCompare(b.customerName));
  });

  // Computed signal for customer filter label
  customerFilterLabel = computed(() => {
    const customerId = this.customerFilter();
    if (customerId === null) {
      return 'Alle kunder';
    }
    const customer = this.availableCustomers().find(c => c.customerId === customerId);
    return customer ? `${customer.customerName} [${customer.customerId}]` : 'Ukendt kunde';
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private toastrService: ToastrService,
    private errorService: ErrorService,
    private adminDashboardService: AdminDashboardService,
    public commonService: CommonService
  ) {}

  ngOnInit(): void {
    // Read query params and set filters
    this.route.queryParams.subscribe(params => {
      // Set time filter from URL (default to 30d)
      if (params['timeFilter'] && ['30d', '7d', '24h'].includes(params['timeFilter'])) {
        this.timeFilter.set(params['timeFilter'] as '30d' | '7d' | '24h');
      }
      
      // Set customer filter from URL
      if (params['customerId']) {
        const customerId = parseInt(params['customerId']);
        if (!isNaN(customerId)) {
          this.customerFilter.set(customerId);
        }
      }
      
      // Set selected error ID from URL
      if (params['errorId']) {
        const errorId = parseInt(params['errorId']);
        if (!isNaN(errorId)) {
          this.selectedErrorId.set(errorId);
        }
      }
    });
  }

  setTimeFilter(filter: '30d' | '7d' | '24h'): void {
    this.timeFilter.set(filter);
    this.searchText.set(''); // Reset search
    this.updateQueryParams();
  }

  onFilteredErrorsChange(filteredErrors: SyncError[]): void {
    this.searchFilteredErrors.set(filteredErrors);
  }

  setCustomerFilter(customerId: number | null): void {
    this.customerFilter.set(customerId);
    this.searchText.set(''); // Reset search
    this.updateQueryParams();
  }

  selectError(error: SyncError): void {
    this.selectedErrorId.set(error.logId);
    this.updateQueryParams();
  }

  truncateText(text: string | null | undefined, maxLength: number = 100): string {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  async copyToClipboard(text: string, fieldName: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      this.toastrService.success(`${fieldName} kopieret til udklipsholder`);
    } catch (err) {
      this.errorService.warning('Kunne ikke kopiere til udklipsholder');
      console.error('Copy failed:', err);
    }
  }

  private updateQueryParams(): void {
    // Only non-default values stay in the URL; null removes a key. Merged, not replaced, so the
    // dashboard shell's own `tab` parameter survives a filter change.
    const queryParams = {
      timeFilter: this.timeFilter() !== '30d' ? this.timeFilter() : null,
      customerId: this.customerFilter(),
      errorId: this.selectedErrorId()
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true  // Replace instead of creating new history entry
    });
  }
}
